import type { PlanTier } from './plans.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AJANIN WEB ARAŞTIRMASI — sözleşme (1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: *"Araştırma neden yapamıyor, webde arayabilmesi lazım, fetch
 * edebilmesi lazım — Claude Code'daki gibi."* Sohbetteki AI "internette arama
 * yapamıyorum, sitene giremiyorum" diyordu; çünkü isteğe hiçbir web aracı
 * konmuyordu.
 *
 * Arama ve sayfa okuma Anthropic'in SUNUCU araçları (`web_search`,
 * `web_fetch`): çağrıyı bizim kodumuz yürütmüyor, sağlayıcı yürütüp sonucu
 * aynı yanıtın içinde döndürüyor. Bizim tarafta kalan üç şey var ve
 * sözleşmesi burada:
 *
 *   · KAYNAKLAR — cevabın altındaki "Kaynaklar" satırı. Sunucu, sağlayıcının
 *     alıntılarından (`web_search_result_location`, okunan sayfanın belge
 *     alıntıları) adres + başlık + alan adı çıkarıyor; ekran yalnız bunu
 *     çiziyor. Araç izi YOK (ürün sahibi, 30 Eyl) — kaynak satırı bir araç
 *     adımı değil, cevabın dayanağı.
 *   · GÜNLÜK TAVAN — arama başına ücret var ($10 / 1000 arama); çalışma
 *     alanı başına günlük tavan plan haklarına bağlanabilir biçimde burada.
 *   · CANLI DURUM — çalışırken göstergede araç adı yok; yalnız adsız
 *     "Araştırıyor" durumu (`AgentLiveActivity`).
 *
 * Güvenlik kuralı (leke) `platform-ops/policy.ts`te: web içeriği okunan
 * turda para / erişim / veri paylaşımı işleri tam otonomide bile karta düşer.
 */

/** Sağlayıcının sunucu araç adları — `server_tool_use.name`. */
export const AGENT_WEB_TOOL_NAMES = ['web_search', 'web_fetch'] as const;
export type AgentWebToolName = (typeof AGENT_WEB_TOOL_NAMES)[number];

/** Cevabın dayandığı tek bir web kaynağı — ekranın "Kaynaklar" satırı. */
export interface AgentWebSource {
  /** Yalnız `http(s)` — başka şema sunucuda eleniyor (bağlantı olarak basılıyor). */
  url: string;
  /** Sayfanın başlığı; sağlayıcı vermediyse `null` (ekran alan adını basar). */
  title: string | null;
  /** `www.` olmadan ana makine adı — satırda kalın görünen kısım. */
  domain: string;
  /**
   * İÇ KAYNAK (Faz 9 X2, 1 Eki 2026) — Mentorivo'nun kendi içeriği: model
   * cevabı yardım merkezi makalesine dayandırıp alıntıladı (`search_result`
   * blokları). `url` o zaman PANEL İÇİ yol (`/help/<slug>`) — ekran çalışma
   * alanının rotasına bağlıyor, aynı sekmede açıyor; `domain` "Mentorivo".
   */
  internal?: 'help';
}

/**
 * Yardım merkezi makalesinin `search_result.source` biçimi — alıntı bu dizeyi
 * AYNEN geri getiriyor (`search_result_location.source`); kaynak satırı
 * buradan panel bağlantısını kuruyor. Yalnız küçük harf/rakam/tire slug.
 */
export const HELP_SOURCE_PREFIX = '/help/';
export function helpSourceOf(slug: string): string {
  return `${HELP_SOURCE_PREFIX}${slug}`;
}
export function helpSlugOfSource(source: unknown): string | null {
  if (typeof source !== 'string' || !source.startsWith(HELP_SOURCE_PREFIX)) return null;
  const slug = source.slice(HELP_SOURCE_PREFIX.length);
  return /^[a-z0-9][a-z0-9-]{0,119}$/.test(slug) ? slug : null;
}

/**
 * Asistan satırının ARAYÜZ kopyasında kaynak bloğu. Modele GİTMEZ (ham
 * sağlayıcı içeriği gider); arayüz bunu okuyup cevabın altına basar.
 */
export const AGENT_SOURCES_BLOCK_TYPE = 'mentorivo_sources';

/**
 * Kullanıcı satırında SUNUCU NOTU — modele giden durum cümlesi (web
 * tavanı doldu, web şu an kapalı). Ekranda görünmez (balon metni değil);
 * modele giderken tek bir metin bloğuna çevrilir. Kullanıcının mesajının
 * SONUNDA durur.
 */
export const AGENT_NOTICE_BLOCK_TYPE = 'mentorivo_notice';

/** Bir satırda saklanan en fazla kaynak — tekrarsız, ilk görülme sırasıyla. */
export const AGENT_WEB_SOURCES_MAX = 20;

/** Ekranın ilk bakışta gösterdiği kaynak sayısı; fazlası "Tümü" ile açılır. */
export const AGENT_WEB_SOURCES_VISIBLE = 6;

/**
 * Canlı turun ADSIZ durumu — SSE `status` karesi. Araç adı TAŞIMAZ: ekran
 * "Düşünüyor" yerine yalnız "Araştırıyor" (web) ya da "Hesaplıyor" (kod
 * çalıştırma, dosya üretimi — Faz 9 X1) yazabilsin diye.
 */
/// Faz 9 X2: `compacting` — sağlayıcı uzun sohbetin eski kısmını özetliyor
/// (sunucu tarafı sıkıştırma); ekran "Sohbeti toparlıyor" yazar.
export type AgentLiveActivity = 'researching' | 'computing' | 'compacting';

/**
 * ÇALIŞMA ALANI BAŞINA GÜNLÜK WEB TAVANI — plan başına.
 *
 * Arama ücretli ($10 / 1000), sayfa okuma yalnız jeton. İki sayaç da
 * çalışma alanının saat diliminde gün başında sıfırlanıyor (Redis). Bugün
 * bütün planlarda aynı — ürün kararı gelince yalnız bu tablo değişir;
 * tavanı okuyan tek yer `AgentWebToolsService`.
 *
 * `-1` = sınırsız (tablo bunu bilerek KULLANMIYOR: bir döngüye giren model
 * sınırsız fatura demek).
 */
export const AGENT_WEB_DAILY_LIMIT: Record<PlanTier, { search: number; fetch: number }> = {
  TRIAL: { search: 100, fetch: 300 },
  STARTER: { search: 100, fetch: 300 },
  GROWTH: { search: 100, fetch: 300 },
  PRO: { search: 100, fetch: 300 },
  ENTERPRISE: { search: 100, fetch: 300 },
};

/** Aboneliği okunamayan çalışma alanının tavanı — sınırsız varsayılmıyor. */
export const AGENT_WEB_DAILY_FALLBACK = { search: 20, fetch: 60 } as const;
